import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, renderList as _renderList, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, normalizeClass as _normalizeClass, unref as _unref, TransitionGroup as _TransitionGroup, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "space-y-8" }
const _hoisted_2 = { class: "mb-2" }
const _hoisted_3 = { class: "text-2xl font-semibold text-gray-800" }
const _hoisted_4 = { class: "text-normal text-gray-400 mt-1" }
const _hoisted_5 = { class: "bg-white rounded-xl border border-gray-200 p-6" }
const _hoisted_6 = { class: "text-base font-semibold text-gray-800 mb-1" }
const _hoisted_7 = { class: "text-sm text-gray-400 mb-4 mt-1" }
const _hoisted_8 = { class: "w-56" }
const _hoisted_9 = { class: "text-sm text-gray-500 mb-4 mt-1" }
const _hoisted_10 = {
  key: 0,
  class: "text-xs text-error-500"
}
const _hoisted_11 = { class: "flex items-center justify-between pt-1" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { class: "flex gap-2" }
const _hoisted_14 = { class: "w-20" }
const _hoisted_15 = { class: "w-44" }
const _hoisted_16 = { class: "bg-white rounded-xl border border-gray-200 p-6" }
const _hoisted_17 = { class: "text-base font-semibold text-gray-800 mb-1" }
const _hoisted_18 = { class: "text-sm text-gray-400 mt-1 mb-4" }
const _hoisted_19 = { class: "w-44" }
const _hoisted_20 = { class: "bg-white rounded-xl border border-gray-200 p-6" }
const _hoisted_21 = { class: "flex items-start justify-between mb-5" }
const _hoisted_22 = { class: "text-base font-semibold text-gray-800" }
const _hoisted_23 = { class: "text-sm text-gray-400 mt-0.5" }
const _hoisted_24 = {
  key: 0,
  class: "space-y-3"
}
const _hoisted_25 = {
  key: 1,
  class: "text-sm text-gray-400 py-1"
}
const _hoisted_26 = { class: "flex-1 min-w-0" }
const _hoisted_27 = { class: "flex items-center gap-2 flex-wrap" }
const _hoisted_28 = { class: "text-sm font-semibold text-gray-800 truncate" }
const _hoisted_29 = {
  key: 0,
  class: "inline-flex items-center gap-1.5 text-xs font-medium text-success-700 bg-success-100 px-2 py-0.5 rounded-full flex-shrink-0"
}
const _hoisted_30 = {
  key: 0,
  class: "flex items-center gap-1 mt-0.5 text-xs text-gray-500"
}
const _hoisted_31 = { class: "flex items-center gap-2 mt-0.5 text-xs text-gray-400" }
const _hoisted_32 = { class: "font-mono" }
const _hoisted_33 = ["src"]
const _hoisted_34 = ["onClick"]

import { reactive, ref, onMounted, onUnmounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow } from 'date-fns';
import { dateFnsLocale } from '@/plugins/i18n/dateFns';
import { Laptop, Smartphone, Monitor, MapPin } from 'lucide-vue-next';
import AppInput from '@/components/ui/AppInput.vue';
import AppButton from '@/components/ui/AppButton.vue';
import { securityApi } from '@/api/security';
import type { Session } from '@/types';

type GeoEntry = { city: string; country: string; countryCode: string; lat: number; lon: number };

export default /*@__PURE__*/_defineComponent({
  __name: 'Index',
  setup(__props) {

const toast = useToast();
const { t, locale } = useI18n();

// ── Change Password ──────────────────────────────────────────────
const pwStep = ref<'idle' | 'code_sent'>('idle');
const pwForm = reactive({ code: '', newPassword: '', confirm: '' });
const pwErrors = reactive({ code: '', newPassword: '', confirm: '', general: '' });
const pwSaving = ref(false);
const resendCooldown = ref(0);
let resendTimer: ReturnType<typeof setInterval> | null = null;

function startResendCooldown() {
    resendCooldown.value = 60;
    resendTimer = setInterval(() => {
        resendCooldown.value--;
        if (resendCooldown.value <= 0 && resendTimer) {
            clearInterval(resendTimer);
            resendTimer = null;
        }
    }, 1000);
}

async function requestPasswordCode() {
    pwSaving.value = true;
    try {
        await securityApi.requestPasswordChangeCode();
        pwStep.value = 'code_sent';
        pwForm.code = '';
        pwErrors.code = '';
        pwErrors.general = '';
        startResendCooldown();
        toast.success(t('account.security.changePassword.codeSent'));
    } catch {
        toast.error(t('errors.unknown'));
    } finally {
        pwSaving.value = false;
    }
}

function cancelPasswordChange() {
    pwStep.value = 'idle';
    pwForm.code = '';
    pwForm.newPassword = '';
    pwForm.confirm = '';
    pwErrors.code = '';
    pwErrors.newPassword = '';
    pwErrors.confirm = '';
    pwErrors.general = '';
    if (resendTimer) { clearInterval(resendTimer); resendTimer = null; }
    resendCooldown.value = 0;
}

async function submitPasswordChange() {
    pwErrors.code = '';
    pwErrors.newPassword = '';
    pwErrors.confirm = '';
    pwErrors.general = '';

    if (!pwForm.code) { pwErrors.code = t('account.security.changePassword.required'); return; }
    if (!pwForm.newPassword) { pwErrors.newPassword = t('account.security.changePassword.required'); return; }
    if (pwForm.newPassword.length < 8) { pwErrors.newPassword = t('account.security.changePassword.tooShort'); return; }
    if (pwForm.newPassword !== pwForm.confirm) { pwErrors.confirm = t('account.security.changePassword.mismatch'); return; }

    pwSaving.value = true;
    try {
        await securityApi.changePassword(pwForm.code, pwForm.newPassword);
        cancelPasswordChange();
        toast.success(t('account.security.changePassword.success'));
    } catch (err: any) {
        const status = err?.response?.status;
        const errorCode = err?.response?.data?.error;
        if (status === 400 && errorCode === 'invalid_code') {
            pwErrors.code = t('account.security.changePassword.invalidCode');
        } else if (status === 422 && errorCode === 'password_too_short') {
            pwErrors.newPassword = t('account.security.changePassword.tooShort');
        } else {
            pwErrors.general = t('errors.unknown');
        }
    } finally {
        pwSaving.value = false;
    }
}

onMounted(async () => {
    await loadSessions();
});

onUnmounted(() => {
    if (resendTimer) clearInterval(resendTimer);
});

// ── Active Sessions ──────────────────────────────────────────────
const sessions = ref<Session[]>([]);
const sessionsLoading = ref(true);

// Geo enrichment
const geoCache = new Map<string, GeoEntry>();
const geoMap = ref(new Map<string, GeoEntry>());

function isPrivateIp(ip: string): boolean {
    return (
        !ip ||
        ip === '::1' ||
        ip.startsWith('127.') ||
        ip.startsWith('192.168.') ||
        ip.startsWith('10.') ||
        /^172\.(1[6-9]|2\d|3[01])\./.test(ip)
    );
}

async function fetchGeo(ip: string): Promise<void> {
    if (isPrivateIp(ip) || geoCache.has(ip)) return;
    try {
        const res = await fetch(`https://ipapi.co/${ip}/json/`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.city && data.country_name && data.country_code) {
            geoCache.set(ip, {
                city: data.city,
                country: data.country_name,
                countryCode: data.country_code,
                lat: data.latitude,
                lon: data.longitude,
            });
            geoMap.value = new Map(geoCache);
        }
    } catch { /* geo is non-critical */ }
}

function countryFlag(code: string): string {
    return [...code.toUpperCase()].map(c => String.fromCodePoint(c.charCodeAt(0) + 0x1F1E6 - 65)).join('');
}

function getLocationText(session: Session): string {
    if (isPrivateIp(session.ip)) return t('account.security.sessions.localNetwork');
    const geo = geoMap.value.get(session.ip);
    if (!geo) return ''; // still resolving — don't duplicate the IP
    return `${geo.city}, ${geo.country} ${countryFlag(geo.countryCode)}`;
}

function getMapUrl(session: Session): string | null {
    const geo = geoMap.value.get(session.ip);
    if (!geo?.lat || !geo?.lon) return null;
    return `https://staticmap.openstreetmap.de/staticmap.php?center=${geo.lat},${geo.lon}&zoom=10&size=220x120&markers=${geo.lat},${geo.lon},lightblue`;
}

function getDeviceIcon(device: string) {
    const d = (device ?? '').toLowerCase();
    if (d.includes('mobile') || d.includes('android') || d.includes('iphone')) return Smartphone;
    if (d.includes('windows') || d.includes('mac') || d.includes('linux')) return Laptop;
    return Monitor;
}

function sessionLabel(session: Session): string {
    const { browser, device } = session;
    if (browser && device) {
        return t('account.security.sessions.deviceLabel', { browser, device });
    }
    return browser || device || t('account.security.sessions.unknownDevice');
}

function formatRelative(dateStr: string): string {
    try {
        return formatDistanceToNow(new Date(dateStr), {
            addSuffix: true,
            locale: dateFnsLocale(locale.value),
        });
    } catch {
        return dateStr;
    }
}

async function loadSessions() {
    sessionsLoading.value = true;
    try {
        sessions.value = await securityApi.getSessions();
        const uniqueIps = [...new Set(sessions.value.map(s => s.ip).filter(ip => ip && !isPrivateIp(ip)))];
        await Promise.allSettled(uniqueIps.map(fetchGeo));
    } catch {
        // Non-fatal
    } finally {
        sessionsLoading.value = false;
    }
}

async function revokeSession(id: string) {
    try {
        await securityApi.revokeSession(id);
        sessions.value = sessions.value.filter((s) => s.id !== id);
        toast.success(t('account.security.sessions.revokeSuccess'));
    } catch {
        toast.error(t('errors.unknown'));
    }
}

async function revokeAll() {
    try {
        await securityApi.revokeAllSessions();
        sessions.value = sessions.value.filter((s) => s.isCurrent);
        toast.success(t('account.security.sessions.revokeAllSuccess'));
    } catch {
        toast.error(t('errors.unknown'));
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("h1", _hoisted_3, _toDisplayString(_ctx.$t('account.security.title')), 1),
      _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.$t('account.security.description')), 1)
    ]),
    _createElementVNode("section", _hoisted_5, [
      _createElementVNode("h2", _hoisted_6, _toDisplayString(_ctx.$t('account.security.changePassword.title')), 1),
      (pwStep.value === 'idle')
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            _createElementVNode("p", _hoisted_7, _toDisplayString(_ctx.$t('account.security.changePassword.description')), 1),
            _createElementVNode("div", _hoisted_8, [
              _createVNode(AppButton, {
                loading: pwSaving.value,
                variant: "secondary",
                onClick: requestPasswordCode
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('account.security.changePassword.sendCode')), 1)
                ]),
                _: 1
              }, 8, ["loading"])
            ])
          ], 64))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            _createElementVNode("p", _hoisted_9, _toDisplayString(_ctx.$t('account.security.changePassword.codeSentInfo')), 1),
            _createElementVNode("form", {
              class: "space-y-3",
              onSubmit: _withModifiers(submitPasswordChange, ["prevent"])
            }, [
              _createVNode(AppInput, {
                modelValue: pwForm.code,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((pwForm.code) = $event)),
                label: _ctx.$t('account.security.changePassword.code'),
                autocomplete: "one-time-code",
                inputmode: "numeric",
                maxlength: "6",
                error: pwErrors.code
              }, null, 8, ["modelValue", "label", "error"]),
              _createVNode(AppInput, {
                modelValue: pwForm.newPassword,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((pwForm.newPassword) = $event)),
                label: _ctx.$t('account.security.changePassword.new'),
                type: "password",
                autocomplete: "new-password",
                error: pwErrors.newPassword
              }, null, 8, ["modelValue", "label", "error"]),
              _createVNode(AppInput, {
                modelValue: pwForm.confirm,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((pwForm.confirm) = $event)),
                label: _ctx.$t('account.security.changePassword.confirm'),
                type: "password",
                autocomplete: "new-password",
                error: pwErrors.confirm
              }, null, 8, ["modelValue", "label", "error"]),
              (pwErrors.general)
                ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(pwErrors.general), 1))
                : _createCommentVNode("", true),
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("button", {
                  type: "button",
                  class: "text-sm text-primary-600 hover:text-primary-700 disabled:opacity-40 transition-colors",
                  disabled: resendCooldown.value > 0,
                  onClick: requestPasswordCode
                }, _toDisplayString(resendCooldown.value > 0 ? _ctx.$t('account.security.changePassword.resendIn', { s: resendCooldown.value }) : _ctx.$t('account.security.changePassword.resend')), 9, _hoisted_12),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, [
                    _createVNode(AppButton, {
                      variant: "white",
                      onClick: cancelPasswordChange
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('account.security.changePassword.cancel')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _createElementVNode("div", _hoisted_15, [
                    _createVNode(AppButton, {
                      loading: pwSaving.value,
                      onClick: submitPasswordChange
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('account.security.changePassword.submit')), 1)
                      ]),
                      _: 1
                    }, 8, ["loading"])
                  ])
                ])
              ])
            ], 32)
          ], 64))
    ]),
    _createElementVNode("section", _hoisted_16, [
      _createElementVNode("h2", _hoisted_17, _toDisplayString(_ctx.$t('account.security.twoFactor.title')), 1),
      _createElementVNode("p", _hoisted_18, _toDisplayString(_ctx.$t('account.security.twoFactor.notAvailable')), 1),
      _createElementVNode("div", _hoisted_19, [
        _createVNode(AppButton, {
          variant: "secondary",
          disabled: true
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_ctx.$t('account.security.twoFactor.enable')), 1)
          ]),
          _: 1
        })
      ])
    ]),
    _createElementVNode("section", _hoisted_20, [
      _createElementVNode("div", _hoisted_21, [
        _createElementVNode("div", null, [
          _createElementVNode("h2", _hoisted_22, _toDisplayString(_ctx.$t('account.security.sessions.title')), 1),
          _createElementVNode("p", _hoisted_23, _toDisplayString(_ctx.$t('account.security.sessions.description')), 1)
        ]),
        (sessions.value.length > 1)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              class: "text-sm font-medium text-error-500 hover:text-error-600 transition-colors flex-shrink-0 mt-0.5",
              onClick: revokeAll
            }, _toDisplayString(_ctx.$t('account.security.sessions.revokeAll')), 1))
          : _createCommentVNode("", true)
      ]),
      (sessionsLoading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(2, (i) => {
              return _createElementVNode("div", {
                key: i,
                class: "animate-pulse rounded-xl border border-gray-100 p-4 flex items-center gap-4"
              }, [...(_cache[4] || (_cache[4] = [
                _createStaticVNode("<div class=\"w-10 h-10 bg-gray-100 rounded-full flex-shrink-0\" data-v-145f4d72></div><div class=\"flex-1 space-y-2\" data-v-145f4d72><div class=\"h-3.5 bg-gray-100 rounded w-40\" data-v-145f4d72></div><div class=\"h-3 bg-gray-100 rounded w-52\" data-v-145f4d72></div><div class=\"h-3 bg-gray-100 rounded w-32\" data-v-145f4d72></div></div>", 2)
              ]))])
            }), 64))
          ]))
        : (sessions.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_25, _toDisplayString(_ctx.$t('account.security.sessions.empty')), 1))
          : (_openBlock(), _createBlock(_TransitionGroup, {
              key: 2,
              name: "session-list",
              tag: "ul",
              class: "space-y-3"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sessions.value, (session) => {
                  return (_openBlock(), _createElementBlock("li", {
                    key: session.id,
                    class: _normalizeClass(["rounded-xl border p-4 flex items-center gap-4 transition-colors duration-150", session.isCurrent ? 'border-success-200 bg-success-50' : 'border-gray-100 bg-gray-50 hover:bg-gray-100'])
                  }, [
                    _createElementVNode("div", {
                      class: _normalizeClass(["w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0", session.isCurrent ? 'bg-success-100 text-success-600' : 'bg-white border border-gray-200 text-gray-500'])
                    }, [
                      (_openBlock(), _createBlock(_resolveDynamicComponent(getDeviceIcon(session.device)), { class: "w-[18px] h-[18px]" }))
                    ], 2),
                    _createElementVNode("div", _hoisted_26, [
                      _createElementVNode("div", _hoisted_27, [
                        _createElementVNode("span", _hoisted_28, _toDisplayString(sessionLabel(session)), 1),
                        (session.isCurrent)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_29, [
                              _cache[5] || (_cache[5] = _createElementVNode("span", { class: "w-1.5 h-1.5 rounded-full bg-success-500 inline-block animate-pulse" }, null, -1)),
                              _createTextVNode(" " + _toDisplayString(_ctx.$t('account.security.sessions.current')), 1)
                            ]))
                          : _createCommentVNode("", true)
                      ]),
                      (getLocationText(session))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_30, [
                            _createVNode(_unref(MapPin), { class: "w-3 h-3 flex-shrink-0" }),
                            _createElementVNode("span", null, _toDisplayString(getLocationText(session)), 1)
                          ]))
                        : _createCommentVNode("", true),
                      _createElementVNode("div", _hoisted_31, [
                        _createElementVNode("span", _hoisted_32, _toDisplayString(session.ip), 1),
                        _cache[6] || (_cache[6] = _createElementVNode("span", { "aria-hidden": "true" }, "·", -1)),
                        _createElementVNode("span", null, _toDisplayString(formatRelative(session.lastActive)), 1)
                      ])
                    ]),
                    (getMapUrl(session))
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: getMapUrl(session)!,
                          alt: "",
                          class: "w-28 h-16 rounded-lg object-cover flex-shrink-0 opacity-80",
                          onError: _cache[3] || (_cache[3] = ($event: any) => (($event.target as HTMLImageElement).style.display = 'none'))
                        }, null, 40, _hoisted_33))
                      : _createCommentVNode("", true),
                    (!session.isCurrent)
                      ? (_openBlock(), _createElementBlock("button", {
                          key: 1,
                          class: "text-xs font-medium text-error-500 hover:text-error-700 hover:bg-error-50 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 border border-transparent hover:border-error-100",
                          onClick: ($event: any) => (revokeSession(session.id))
                        }, _toDisplayString(_ctx.$t('account.security.sessions.revokeOne')), 9, _hoisted_34))
                      : _createCommentVNode("", true)
                  ], 2))
                }), 128))
              ]),
              _: 1
            }))
    ])
  ]))
}
}

})